import { useEffect, useMemo, useState } from 'react';
import {
	QueryFunctionContext,
	useInfiniteQuery,
	useMutation,
	useQueryClient,
} from 'react-query';
import { useDebounce } from '../../../common/hooks/useDebounce';
import { LIST_LIMIT } from '../../../constants';
import { listManufacturers } from '../../../services/manufacturer.service';
import { Manufacturer } from '../Manufacturer.type';

const getManufacturersQuery = (
	definitelyInclude?: string,
	publicRoute?: boolean
) => {
	return async (context: QueryFunctionContext) => {
		const search = context.queryKey[1] as string;
		return listManufacturers(
			{
				skip: context.pageParam,
				limit: LIST_LIMIT,
				search,
				include:
					definitelyInclude && (!context.pageParam || context.pageParam === 0)
						? definitelyInclude
						: undefined,
			},
			publicRoute
		);
	};
};

const useManufacturers = (
	includeNone: boolean = false,
	definitelyInclude?: string,
	publicRoute?: boolean
) => {
	const queryClient = useQueryClient();
	const [queryKey, setQueryKey] = useState<[string, string | undefined]>([
		'manufacturers',
		undefined,
	]);
	const {
		status,
		data,
		error,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		refetch,
	} = useInfiniteQuery<{
		data: Manufacturer[];
		metaData: { skip: number; totalRecords: number };
	}>(queryKey, getManufacturersQuery(definitelyInclude, publicRoute), {
		getNextPageParam: (lastGroup) => lastGroup?.metaData?.skip || undefined,
		refetchOnWindowFocus: false,
	});

	const [searchTerm, setSearchTerm] = useState<string>();
	const debouncedSearchTerm = useDebounce(searchTerm);

	useEffect(() => {
		setQueryKey(['manufacturers', debouncedSearchTerm]);
	}, [debouncedSearchTerm]);

	// Parse selected IDs from definitelyInclude prop
	const selectedIds = useMemo(() => {
		if (!definitelyInclude) return [];
		return definitelyInclude
			.split(',')
			.map(id => parseInt(id))
			.filter(id => !isNaN(id) && id > 0);
	}, [definitelyInclude]);

	// Process and sort manufacturer options
	const manufacturerOptions = useMemo(() => {
		const allData = data?.pages.flatMap((page) => page.data) || [];

		// Deduplicate using Map (keeps first occurrence) - filter out invalid items
		const uniqueManufacturers = Array.from(
			new Map(
				allData
					.filter(item => item && item.id && item.id !== 0)
					.map(item => [item.id, item])
			).values()
		);

		// Separate and combine: selected first (maintaining order), then unselected
		const selected = uniqueManufacturers.filter(m =>
			typeof m.id === 'number' && selectedIds.includes(m.id)
		);
		const unselected = uniqueManufacturers.filter(m =>
			!(typeof m.id === 'number' && selectedIds.includes(m.id))
		);
		const sorted = [...selected, ...unselected];

		// Add special options at the beginning
		const specialOptions = includeNone
			? [{ name: 'None', id: undefined }, { name: 'Add new', id: '$add' }]
			: [{ name: 'Add new', id: '$add' }];

		return [...specialOptions, ...sorted];
	}, [data, includeNone, selectedIds]);

	const onSearch = (val: string) => {
		setSearchTerm(val);
	};

	const onAddManufacturer = (val: Manufacturer) => {
		queryClient.invalidateQueries('manufacturers');
	};

	return {
		status,
		data,
		error,
		isFetching,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		refetch,
		onSearch,
		manufacturerOptions,
		searchTerm,
		onAddManufacturer,
	};
};

export default useManufacturers;
